"use client";

import { useState, useMemo, useCallback } from "react";
import { cn } from "~/widgets/lib/utils";
import { FilterOverlayPortal } from "./FilterOverlayPortal";
import type {
  TimetableCourse,
  TimetableLocation,
  TimetableTutor,
} from "~/widgets/lib/api";

type AvailabilityFilter = "all" | "sold-out" | "available" | "few-spots";

type DateRange = {
  from: Date;
  to: Date;
};

const AVAILABILITY_OPTIONS: { value: AvailabilityFilter; label: string }[] = [
  { value: "all", label: "All availability" },
  { value: "available", label: "Available" },
  { value: "few-spots", label: "Few spots left" },
  { value: "sold-out", label: "Sold out" },
];

// Icon components
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M2 6l3 3 5-5" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="8" x2="21" y1="6" y2="6" />
      <line x1="8" x2="21" y1="12" y2="12" />
      <line x1="8" x2="21" y1="18" y2="18" />
      <line x1="3" x2="3.01" y1="6" y2="6" />
      <line x1="3" x2="3.01" y1="12" y2="12" />
      <line x1="3" x2="3.01" y1="18" y2="18" />
    </svg>
  );
}

function TicketIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2" />
      <path d="M13 17v2" />
      <path d="M13 11v2" />
    </svg>
  );
}

function MapIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
    </svg>
  );
}

function CalendarRangeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
      <path d="M17 14h-6" />
      <path d="M13 18H7" />
      <path d="M7 14h.01" />
      <path d="M17 18h.01" />
    </svg>
  );
}

export type MobileFilterOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
  courses: TimetableCourse[];
  locations: TimetableLocation[];
  tutors: TimetableTutor[];
  selectedCourseIds: string[];
  selectedLocationIds: string[];
  selectedTutorIds: string[];
  selectedAvailability: AvailabilityFilter;
  onCourseChange: (ids: string[]) => void;
  onLocationChange: (ids: string[]) => void;
  onTutorChange: (ids: string[]) => void;
  onAvailabilityChange: (value: AvailabilityFilter) => void;
  onReset: () => void;
  primaryColor?: string;
  /** Business slug for loading theme overrides */
  businessSlug?: string;
  /** API URL for loading theme overrides */
  apiUrl?: string;
};

// Menu button component for filter selection
function FilterMenuButton({
  icon: Icon,
  label,
  value,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-gray-500" />
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500">{label}</span>
          <span className="text-sm font-medium text-gray-900">{value}</span>
        </div>
      </div>
      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
    </button>
  );
}

// Nested filter sheet for selecting options
function NestedSheet({
  isOpen,
  onClose,
  title,
  children,
  showSearch = false,
  searchPlaceholder = "Search...",
  searchValue = "",
  onSearchChange,
  selectedItems = [],
  onRemoveItem,
  onClearAll,
  onDone,
  showFooter = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  selectedItems?: { value: string; label: string }[];
  onRemoveItem?: (value: string) => void;
  onClearAll?: () => void;
  onDone?: () => void;
  showFooter?: boolean;
}) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sheet */}
      <div className="absolute inset-x-0 top-0 bg-white rounded-b-2xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header with back button */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full -ml-2"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>

        {/* Search Input */}
        {showSearch && onSearchChange && (
          <div className="px-4 pt-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white text-gray-900"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {/* Selected Items & Footer */}
        {showFooter && (
          <div className="border-t border-gray-200">
            {/* Selected Items Chips */}
            {selectedItems.length > 0 && (
              <div className="px-4 pt-4 pb-2">
                <div className="flex flex-wrap gap-2">
                  {selectedItems.map((item) => (
                    <div
                      key={item.value}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm"
                    >
                      <span className="truncate max-w-[200px]">{item.label}</span>
                      <button
                        type="button"
                        onClick={() => onRemoveItem?.(item.value)}
                        className="p-0.5 hover:bg-gray-100 rounded-full"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                {onClearAll && selectedItems.length > 0 && (
                  <button
                    type="button"
                    onClick={onClearAll}
                    className="mt-3 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}

            {/* Done Button */}
            {onDone && (
              <div className="p-4">
                <button
                  type="button"
                  onClick={onDone}
                  className="w-full px-4 py-3 text-sm font-medium text-primary-foreground rounded-lg bg-primary hover:bg-primary/90 transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Option button component
function OptionButton({
  isSelected,
  isRadio = false,
  label,
  onClick,
}: {
  isSelected: boolean;
  isRadio?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors",
        isSelected ? "bg-gray-100" : "hover:bg-gray-50"
      )}
    >
      <span
        className={cn(
          "w-5 h-5 border flex items-center justify-center shrink-0",
          isRadio ? "rounded-full" : "rounded",
          isSelected ? "bg-gray-900 border-gray-900 text-white" : "border-gray-300"
        )}
      >
        {isSelected && <CheckIcon className="text-white" />}
      </span>
      <span className="text-sm text-gray-900">{label}</span>
    </button>
  );
}

export function MobileFilterOverlay({
  isOpen,
  onClose,
  courses,
  locations,
  tutors,
  selectedCourseIds,
  selectedLocationIds,
  selectedTutorIds,
  selectedAvailability,
  onCourseChange,
  onLocationChange,
  onTutorChange,
  onAvailabilityChange,
  onReset,
  primaryColor,
  businessSlug,
  apiUrl,
}: MobileFilterOverlayProps) {
  // Nested sheet states
  const [activeSheet, setActiveSheet] = useState<
    "event" | "availability" | "location" | "tutor" | null
  >(null);

  // Pending filter values (applied on close)
  const [pendingCourseIds, setPendingCourseIds] = useState(selectedCourseIds);
  const [pendingLocationIds, setPendingLocationIds] = useState(selectedLocationIds);
  const [pendingTutorIds, setPendingTutorIds] = useState(selectedTutorIds);
  const [pendingAvailability, setPendingAvailability] = useState(selectedAvailability);

  // Search states
  const [eventSearch, setEventSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [tutorSearch, setTutorSearch] = useState("");

  // Sync pending values when sheet opens
  useMemo(() => {
    if (isOpen) {
      setPendingCourseIds(selectedCourseIds);
      setPendingLocationIds(selectedLocationIds);
      setPendingTutorIds(selectedTutorIds);
      setPendingAvailability(selectedAvailability);
    }
  }, [isOpen, selectedCourseIds, selectedLocationIds, selectedTutorIds, selectedAvailability]);

  const handleCloseNestedSheet = useCallback(() => {
    setEventSearch("");
    setLocationSearch("");
    setTutorSearch("");
    setActiveSheet(null);
  }, []);

  // Options
  const courseOptions = courses.map((c) => ({ value: c._id, label: c.title }));
  const locationOptions = locations.map((l) => ({ value: l._id, label: l.name }));
  const tutorOptions = tutors.map((t) => ({
    value: t._id,
    label: t.fullName || t.name || "Unknown",
  }));

  // Filtered options based on search
  const filteredCourseOptions = useMemo(() => {
    if (!eventSearch.trim()) return courseOptions;
    return courseOptions.filter((o) => o.label.toLowerCase().includes(eventSearch.toLowerCase()));
  }, [courseOptions, eventSearch]);

  const filteredLocationOptions = useMemo(() => {
    if (!locationSearch.trim()) return locationOptions;
    return locationOptions.filter((o) => o.label.toLowerCase().includes(locationSearch.toLowerCase()));
  }, [locationOptions, locationSearch]);

  const filteredTutorOptions = useMemo(() => {
    if (!tutorSearch.trim()) return tutorOptions;
    return tutorOptions.filter((o) => o.label.toLowerCase().includes(tutorSearch.toLowerCase()));
  }, [tutorOptions, tutorSearch]);

  // Selected items for chips
  const selectedCourseItems = useMemo(() => {
    return pendingCourseIds
      .map((id) => courseOptions.find((o) => o.value === id))
      .filter(Boolean) as { value: string; label: string }[];
  }, [pendingCourseIds, courseOptions]);

  const selectedLocationItems = useMemo(() => {
    return pendingLocationIds
      .map((id) => locationOptions.find((o) => o.value === id))
      .filter(Boolean) as { value: string; label: string }[];
  }, [pendingLocationIds, locationOptions]);

  const selectedTutorItems = useMemo(() => {
    return pendingTutorIds
      .map((id) => tutorOptions.find((o) => o.value === id))
      .filter(Boolean) as { value: string; label: string }[];
  }, [pendingTutorIds, tutorOptions]);

  // Display text helpers
  const getEventText = useCallback(() => {
    if (pendingCourseIds.length === 0) return "All Events";
    if (pendingCourseIds.length === 1) {
      const course = courses.find((c) => c._id === pendingCourseIds[0]);
      return course?.title || "1 selected";
    }
    return `${pendingCourseIds.length} selected`;
  }, [pendingCourseIds, courses]);

  const getAvailabilityText = useCallback(() => {
    const option = AVAILABILITY_OPTIONS.find((o) => o.value === pendingAvailability);
    return option?.label || "All availability";
  }, [pendingAvailability]);

  const getLocationText = useCallback(() => {
    if (pendingLocationIds.length === 0) return "All Locations";
    if (pendingLocationIds.length === 1) {
      const location = locations.find((l) => l._id === pendingLocationIds[0]);
      return location?.name || "1 selected";
    }
    return `${pendingLocationIds.length} selected`;
  }, [pendingLocationIds, locations]);

  const getTutorText = useCallback(() => {
    if (pendingTutorIds.length === 0) return "All Tutors";
    if (pendingTutorIds.length === 1) {
      const tutor = tutors.find((t) => t._id === pendingTutorIds[0]);
      return tutor?.fullName || tutor?.name || "1 selected";
    }
    return `${pendingTutorIds.length} selected`;
  }, [pendingTutorIds, tutors]);

  const handleApplyFilters = () => {
    onCourseChange(pendingCourseIds);
    onLocationChange(pendingLocationIds);
    onTutorChange(pendingTutorIds);
    onAvailabilityChange(pendingAvailability);
    onClose();
  };

  const handleResetFilters = () => {
    setPendingCourseIds([]);
    setPendingLocationIds([]);
    setPendingTutorIds([]);
    setPendingAvailability("all");
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <FilterOverlayPortal isOpen={isOpen} onClose={onClose} primaryColor={primaryColor} businessSlug={businessSlug} apiUrl={apiUrl}>
      {/* Main Filter Menu */}
      {activeSheet === null && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Filter Menu Buttons */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {courseOptions.length > 0 && (
              <FilterMenuButton
                icon={ListIcon}
                label="Event Type"
                value={getEventText()}
                onClick={() => setActiveSheet("event")}
              />
            )}

            <FilterMenuButton
              icon={TicketIcon}
              label="Availability"
              value={getAvailabilityText()}
              onClick={() => setActiveSheet("availability")}
            />

            {locationOptions.length > 1 && (
              <FilterMenuButton
                icon={MapIcon}
                label="Location"
                value={getLocationText()}
                onClick={() => setActiveSheet("location")}
              />
            )}

            {tutorOptions.length > 1 && (
              <FilterMenuButton
                icon={UserIcon}
                label="Tutor"
                value={getTutorText()}
                onClick={() => setActiveSheet("tutor")}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleResetFilters}
              className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 bg-white text-gray-900"
            >
              Reset All
            </button>
            <button
              type="button"
              onClick={handleApplyFilters}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground rounded-lg bg-primary hover:bg-primary/90"
            >
              Apply Filters
            </button>
          </div>
        </>
      )}

      {/* Event Type Sheet */}
      <NestedSheet
        isOpen={activeSheet === "event"}
        onClose={handleCloseNestedSheet}
        title="Event Type"
        showSearch={courseOptions.length > 5}
        searchPlaceholder="Search events..."
        searchValue={eventSearch}
        onSearchChange={setEventSearch}
        selectedItems={selectedCourseItems}
        onRemoveItem={(value) => setPendingCourseIds(pendingCourseIds.filter((id) => id !== value))}
        onClearAll={() => setPendingCourseIds([])}
        onDone={handleCloseNestedSheet}
        showFooter={true}
      >
        <div className="space-y-1">
          <OptionButton
            isSelected={pendingCourseIds.length === 0}
            isRadio
            label="All Events"
            onClick={() => setPendingCourseIds([])}
          />
          {filteredCourseOptions.map((option) => (
            <OptionButton
              key={option.value}
              isSelected={pendingCourseIds.includes(option.value)}
              label={option.label}
              onClick={() =>
                setPendingCourseIds(
                  pendingCourseIds.includes(option.value)
                    ? pendingCourseIds.filter((id) => id !== option.value)
                    : [...pendingCourseIds, option.value]
                )
              }
            />
          ))}
        </div>
      </NestedSheet>

      {/* Availability Sheet */}
      <NestedSheet
        isOpen={activeSheet === "availability"}
        onClose={() => setActiveSheet(null)}
        title="Availability"
      >
        <div className="space-y-1">
          {AVAILABILITY_OPTIONS.map((option) => (
            <OptionButton
              key={option.value}
              isSelected={pendingAvailability === option.value}
              isRadio
              label={option.label}
              onClick={() => {
                setPendingAvailability(option.value);
                setActiveSheet(null);
              }}
            />
          ))}
        </div>
      </NestedSheet>

      {/* Location Sheet */}
      <NestedSheet
        isOpen={activeSheet === "location"}
        onClose={handleCloseNestedSheet}
        title="Location"
        showSearch={locationOptions.length > 5}
        searchPlaceholder="Search locations..."
        searchValue={locationSearch}
        onSearchChange={setLocationSearch}
        selectedItems={selectedLocationItems}
        onRemoveItem={(value) => setPendingLocationIds(pendingLocationIds.filter((id) => id !== value))}
        onClearAll={() => setPendingLocationIds([])}
        onDone={handleCloseNestedSheet}
        showFooter={true}
      >
        <div className="space-y-1">
          <OptionButton
            isSelected={pendingLocationIds.length === 0}
            isRadio
            label="All Locations"
            onClick={() => setPendingLocationIds([])}
          />
          {filteredLocationOptions.map((option) => (
            <OptionButton
              key={option.value}
              isSelected={pendingLocationIds.includes(option.value)}
              label={option.label}
              onClick={() =>
                setPendingLocationIds(
                  pendingLocationIds.includes(option.value)
                    ? pendingLocationIds.filter((id) => id !== option.value)
                    : [...pendingLocationIds, option.value]
                )
              }
            />
          ))}
        </div>
      </NestedSheet>

      {/* Tutor Sheet */}
      <NestedSheet
        isOpen={activeSheet === "tutor"}
        onClose={handleCloseNestedSheet}
        title="Tutor"
        showSearch={tutorOptions.length > 5}
        searchPlaceholder="Search tutors..."
        searchValue={tutorSearch}
        onSearchChange={setTutorSearch}
        selectedItems={selectedTutorItems}
        onRemoveItem={(value) => setPendingTutorIds(pendingTutorIds.filter((id) => id !== value))}
        onClearAll={() => setPendingTutorIds([])}
        onDone={handleCloseNestedSheet}
        showFooter={true}
      >
        <div className="space-y-1">
          <OptionButton
            isSelected={pendingTutorIds.length === 0}
            isRadio
            label="All Tutors"
            onClick={() => setPendingTutorIds([])}
          />
          {filteredTutorOptions.map((option) => (
            <OptionButton
              key={option.value}
              isSelected={pendingTutorIds.includes(option.value)}
              label={option.label}
              onClick={() =>
                setPendingTutorIds(
                  pendingTutorIds.includes(option.value)
                    ? pendingTutorIds.filter((id) => id !== option.value)
                    : [...pendingTutorIds, option.value]
                )
              }
            />
          ))}
        </div>
      </NestedSheet>
    </FilterOverlayPortal>
  );
}

// Date Range Overlay
export type MobileDateRangeOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  primaryColor?: string;
  /** Business slug for loading theme overrides */
  businessSlug?: string;
  /** API URL for loading theme overrides */
  apiUrl?: string;
};

// Date helper functions
function addWeeks(date: Date, weeks: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function MobileDateRangeOverlay({
  isOpen,
  onClose,
  dateRange,
  onDateRangeChange,
  primaryColor,
  businessSlug,
  apiUrl,
}: MobileDateRangeOverlayProps) {
  const [localRange, setLocalRange] = useState(dateRange);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1)
  );
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempStart, setTempStart] = useState<Date | null>(null);

  // Sync when sheet opens
  useMemo(() => {
    if (isOpen) {
      setLocalRange(dateRange);
      setCurrentMonth(new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1));
      setTempStart(null);
      setSelectingStart(true);
    }
  }, [isOpen, dateRange]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = isSameDay(localRange.from, today) && isSameDay(localRange.to, today);
  const isTwoWeeks = isSameDay(localRange.from, today) && isSameDay(localRange.to, addWeeks(today, 2));
  const isThreeMonths = isSameDay(localRange.from, today) && isSameDay(localRange.to, addMonths(today, 3));
  const isSixMonths = isSameDay(localRange.from, today) && isSameDay(localRange.to, addMonths(today, 6));

  const setQuickRange = (weeks?: number, months?: number, isTodayOnly?: boolean) => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    let to: Date;

    if (isTodayOnly) {
      to = new Date(from);
    } else if (weeks) {
      to = addWeeks(from, weeks);
    } else if (months) {
      to = addMonths(from, months);
    } else {
      to = new Date(from);
    }

    setLocalRange({ from, to });
    setCurrentMonth(new Date(from.getFullYear(), from.getMonth(), 1));
    setTempStart(null);
    setSelectingStart(true);
  };

  const handleApply = () => {
    // If the user only clicked a single date (tempStart set, no second click yet),
    // treat that single click as the new start and reuse the existing end (or use it as both).
    const rangeToApply = tempStart
      ? { from: tempStart, to: tempStart > localRange.to ? tempStart : localRange.to }
      : localRange;
    onDateRangeChange(rangeToApply);
    onClose();
  };

  const handleReset = () => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = addMonths(from, 12);
    setLocalRange({ from, to });
    setCurrentMonth(new Date(from.getFullYear(), from.getMonth(), 1));
  };

  // Calendar rendering
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const monthName = currentMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const days: (Date | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
  }

  const isInRange = (date: Date) => {
    const start = tempStart || localRange.from;
    return date >= start && date <= localRange.to;
  };

  const isStart = (date: Date) => {
    const start = tempStart || localRange.from;
    return isSameDay(date, start);
  };

  const isEnd = (date: Date) => isSameDay(date, localRange.to);

  const isPast = (date: Date) => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    return date < todayMidnight;
  };

  const handleDayClick = (date: Date) => {
    if (isPast(date)) return;

    if (selectingStart) {
      setTempStart(date);
      setSelectingStart(false);
    } else {
      const start = tempStart || localRange.from;
      if (date < start) {
        setLocalRange({ from: date, to: start });
      } else {
        setLocalRange({ from: start, to: date });
      }
      setTempStart(null);
      setSelectingStart(true);
    }
  };

  if (!isOpen) return null;

  return (
    <FilterOverlayPortal isOpen={isOpen} onClose={onClose} primaryColor={primaryColor} businessSlug={businessSlug} apiUrl={apiUrl}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Select Date Range</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-3">
        {/* Quick Select - Dropdown for mobile */}
        <div className="space-y-1.5">
          <label htmlFor="quick-select" className="font-medium text-sm text-gray-900">Quick Select</label>
          <select
            id="quick-select"
            value={
              isToday ? "today" :
              isTwoWeeks ? "2weeks" :
              isThreeMonths ? "3months" :
              isSixMonths ? "6months" :
              "custom"
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value === "today") setQuickRange(undefined, undefined, true);
              else if (value === "2weeks") setQuickRange(2);
              else if (value === "3months") setQuickRange(undefined, 3);
              else if (value === "6months") setQuickRange(undefined, 6);
            }}
            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
              backgroundSize: "1rem",
              paddingRight: "2.5rem",
            }}
          >
            <option value="today">Today</option>
            <option value="2weeks">Next 2 Weeks</option>
            <option value="3months">Next 3 Months</option>
            <option value="6months">Next 6 Months</option>
            <option value="custom" disabled>Custom Range</option>
          </select>
        </div>

        {/* Calendar */}
        <div className="p-2">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeftIcon />
            </button>
            <span className="font-medium text-gray-900">{monthName}</span>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronRightIcon />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="h-9" />;
              }

              const isDisabled = isPast(date);
              const inRange = isInRange(date);
              const rangeStart = isStart(date);
              const rangeEnd = isEnd(date);

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleDayClick(date)}
                  className={cn(
                    "h-9 w-full text-sm rounded-md transition-colors",
                    isDisabled && "text-gray-300 cursor-not-allowed",
                    !isDisabled && !inRange && "hover:bg-gray-100 text-gray-900",
                    inRange && !rangeStart && !rangeEnd && "bg-gray-100 text-gray-900",
                    (rangeStart || rangeEnd) && "bg-primary text-primary-foreground"
                  )}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Selected range display and prompt */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-center gap-3 text-sm">
              <div className="flex flex-col items-center">
                <span className="text-xs text-gray-500">Start</span>
                <span className={cn(
                  "font-medium",
                  tempStart ? "text-primary" : "text-gray-900"
                )}>
                  {(tempStart || localRange.from).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                </span>
              </div>
              <span className="text-gray-400">→</span>
              <div className="flex flex-col items-center">
                <span className="text-xs text-gray-500">End</span>
                <span className="font-medium text-gray-900">
                  {localRange.to.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                </span>
              </div>
            </div>
            {/* Only show prompt when actively selecting (tempStart means user picked a new start, waiting for end) */}
            {tempStart && (
              <p className="text-xs text-primary text-center font-medium">
                Tap a date to set end
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-3 p-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleReset}
          className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 bg-white text-gray-900"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground rounded-lg bg-primary hover:bg-primary/90"
        >
          Apply
        </button>
      </div>
    </FilterOverlayPortal>
  );
}
